import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { InputAdornment, MenuItem, IconButton } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";
import { IdText } from "@/models/apiModels";
import { AccountingFilterRequest } from "@/models/accounting";
import { AccountingDocType } from "@/models/enums";
import BaseTooltip from "@/components/common/baseTooltip";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import BaseInput from "@/components/common/baseInput";
import BaseDateInput from "@/components/common/baseDateInput";
import SearchIcon from "@mui/icons-material/Search";
import BaseButton from "../common/baseButton";
import filtersStore from "@/stores/filtersStore";
import ClearIcon from "@/assets/icons/clear-icon.svg";

interface AccountingFilterProps {
    total: number;
    searchClick: Function;
    filter: AccountingFilterRequest;
    setFilter: Function;
    filterTabs: JSX.Element;
    collapseFilterClick: Function;
}

function AccountingFilter({ searchClick, filter, setFilter, filterTabs, collapseFilterClick }: AccountingFilterProps) {
    const { t } = useTranslation();
    const [hiddenFilter, setHiddenFilter] = useState(false);
    const docTypeList: IdText[] = (
        Object.values(AccountingDocType).filter((val) => isNaN(Number(val))) as (keyof typeof AccountingDocType)[]
    ).map((key) => new IdText(AccountingDocType[key].toString(), key));

    const setHiddenFilterData = () => {
        setHiddenFilter(!hiddenFilter);
        collapseFilterClick();
        filtersStore.setAccountingHiddenFilter(!hiddenFilter);
    };

    function clearFilter() {
        filtersStore.setAccountingHiddenFilter(null);
        setFilter(() => ({
            ...new AccountingFilterRequest(),
        }));
        searchClick();
    }

    function fetchHiddenFilter() {
        if (filtersStore.accountingHiddenFilter != null) {
            setHiddenFilter(filtersStore.accountingHiddenFilter);
        }
    }

    useEffect(() => {
        let ignore = false;
        if (!ignore) {
            fetchHiddenFilter();
        }
        return () => {
            ignore = true;
        };
    }, []);

    const secondFilterRow = hiddenFilter ? (
        <div className="accounting-filter-second-row">
            <div className="accounting-filter-fields">
                <div>
                    <BaseInput
                        select
                        label={t("accounting.docType")}
                        size="small"
                        value={filter.docType || ""}
                        onChange={(event) =>
                            setFilter(() => ({
                                ...filter,
                                docType: event.target.value,
                            }))
                        }
                        sx={{
                            width: "250px",
                            fontSize: 16,
                        }}
                        InputProps={{
                            endAdornment: (
                                <IconButton
                                    className="select-clear-icon"
                                    sx={{
                                        visibility: filter.docType ? "visible" : "hidden",
                                    }}
                                    onClick={() =>
                                        setFilter(() => ({
                                            ...filter,
                                            docType: null,
                                        }))
                                    }
                                >
                                    <img src={ClearIcon} alt="Clear icon" />
                                </IconButton>
                            ),
                        }}
                    >
                        {docTypeList.map((docType) => (
                            <MenuItem key={docType.id} value={docType.text}>
                                {docType.text}
                            </MenuItem>
                        ))}
                    </BaseInput>
                </div>
            </div>
        </div>
    ) : (
        ""
    );

    function handleSubmit(event: any) {
        event.preventDefault();
        searchClick();
    }

    return (
        <div id="accounting-filter-block" className="accounting-filter-block">
            <div className="accounting-filter">
                <form onSubmit={handleSubmit}>
                    <div className="font-style accounting-span">{t("accounting.FilterHeader")}</div>
                    <div className="accounting-filter-first-row">
                        <div className="accounting-filter-fields">
                            <div>
                                <BaseInput
                                    id="keyword-input"
                                    className="search-input"
                                    placeholder={`${t("accounting.FilterSearch")}`}
                                    type="search"
                                    size="small"
                                    value={filter.keywords}
                                    onChange={(event) =>
                                        setFilter(() => ({
                                            ...filter,
                                            keywords: event.target.value,
                                        }))
                                    }
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        width: "397px",
                                    }}
                                />
                            </div>
                            <div>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DesktopDatePicker
                                        className="date-picker-input"
                                        label={t("accounting.fromDate")}
                                        inputFormat="DD/MM/YYYY"
                                        value={filter.fromDate}
                                        onChange={(newValue: Dayjs | null) =>
                                            setFilter(() => ({
                                                ...filter,
                                                fromDate: newValue,
                                            }))
                                        }
                                        renderInput={(params) => (
                                            <BaseDateInput
                                                {...params}
                                                sx={{
                                                    width: "200px",
                                                }}
                                            />
                                        )}
                                    />
                                </LocalizationProvider>
                            </div>
                            <div>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DesktopDatePicker
                                        className="date-picker-input"
                                        label={t("accounting.endDate")}
                                        inputFormat="DD/MM/YYYY"
                                        value={filter.toDate}
                                        onChange={(newValue: Dayjs | null) =>
                                            setFilter(() => ({
                                                ...filter,
                                                toDate: newValue,
                                            }))
                                        }
                                        renderInput={(params) => (
                                            <BaseDateInput
                                                {...params}
                                                sx={{
                                                    width: "200px",
                                                }}
                                            />
                                        )}
                                    />
                                </LocalizationProvider>
                            </div>
                            <div>
                                <BaseTooltip
                                    title={!hiddenFilter ? t("orders.FilterButtonShow") : t("orders.FilterButtonHide")}
                                    placement="bottom"
                                >
                                    <IconButton
                                        className="show-hidden-filter-button-div"
                                        onClick={() => setHiddenFilterData()}
                                        type="button"
                                    >
                                        {hiddenFilter ? (
                                            <KeyboardDoubleArrowUpIcon className="show-hidden-filter-button" />
                                        ) : (
                                            <KeyboardDoubleArrowDownIcon className="show-hidden-filter-button" />
                                        )}
                                    </IconButton>
                                </BaseTooltip>
                            </div>
                        </div>
                        <div className="filter-search-btn">
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                }}
                            >
                                <span
                                    onClick={() => clearFilter()}
                                    className="link-element clear-all-btn"
                                    style={{
                                        minWidth: "70px",
                                    }}
                                >
                                    {t("display.ClearAll")}
                                </span>
                            </div>
                            <BaseButton className="basic-button" type="submit" style={{ padding: "0px 30px" }}>
                                {t("general.filter")}
                            </BaseButton>
                        </div>
                    </div>
                    {secondFilterRow}
                </form>
            </div>
            {filterTabs}
        </div>
    );
}

export default AccountingFilter;
